import React from 'react';
import { Form, Button, message, Typography, Input } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import DrawerHookForm from '../../../global/drawer/DrawerHookForm';
import { ApplicationState } from '../../../../reducers';
import FormInput from '../../../global/Forms/FormInput';
import FormSelect from '../../../global/Forms/FormSelect';
import { updateCustomField } from '../../../../actions/brand/custom_fields/index';
import { AsyncDispatch } from '../../../../../types/global';
import {
  BrandCustomField,
  CustomFieldType,
  CustomFieldOption,
  CustomFieldGroup,
} from '../../../../../types/custom_fields';
import SettingsCustomFieldOptions from '../../settings/SettingsCustomFieldOptions';
import FormSwitch from '../../../global/Forms/FormSwitch';
import AntTooltip from '../../../global/AntTooltip';
import { useAppDispatch } from '../../../../store/ReduxStore';
import {
  getTableDelimiterValidation,
  getTableRowDelimiterValidation,
  getProductRelationDelimiterValidation,
  getProductRelationBrandCodeDelimiterValidation,
} from '../../../../utils/CustomFieldValidationSchemas';

type FormData = {
  name: string;
  description: string;
  customFieldTypeId: number;
  options: CustomFieldOption[];
  headers: CustomFieldOption[];
  tableDelimiter: string;
  tableRowDelimiter: string;
  productRelationDelimiter: string;
  productRelationBrandCodeDelimiter: string | null;
  publishChanges: boolean;
  scriptId: number | null;
  groupId: number | string | null;
  enhancedValidationIds: number[];
};

type ParentCustomFieldEditDrawerProps = {
  visible: boolean;
  canEditCustomFields: boolean;
  closeDrawer: () => void;
  onFieldSaved: () => void;
  customFieldsLength: number;
  customField?: BrandCustomField;
  customFields: BrandCustomField[];
  groups: CustomFieldGroup[];
  onDelete?: (customFieldId: number) => void;
  isDeleting?: boolean;
};

const ParentCustomFieldEditDrawer: React.FC<ParentCustomFieldEditDrawerProps> = ({
  visible,
  canEditCustomFields,
  closeDrawer,
  onFieldSaved,
  customFieldsLength,
  customField,
  customFields,
  groups,
  onDelete,
  isDeleting,
}) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const customFieldTypes = useSelector(
    (state: ApplicationState) => state.resources.data.custom_field?.types || []
  );
  const scripts = useSelector((state: ApplicationState) => state.distribution.apiSettings.scripts);
  const fetchingScripts = useSelector(
    (state: ApplicationState) => state.distribution.apiSettings.fetchingScripts
  );
  const enhancedValidations = useSelector(
    (state: ApplicationState) => state.brand.customFields.enhancedValidations || []
  );

  const groupOptions = React.useMemo(
    () =>
      groups.map(g => ({
        ...g,
        id: g.id === 'other' || typeof g.id === 'number' ? g.id : Number(g.id.substring(1)),
      })),
    [groups]
  );

  const getValidationSchema = () => {
    return Yup.object().shape({
      name: Yup.string()
        .required(t('settings:customFields.nameRequired'))
        .test(
          'check-unique-name',
          t('settings:customFields.validation.customFieldIsUsed'),
          (val: any) => {
            return !customField || (customField && customField.name !== val)
              ? customFields.every(({ name }) => name !== val)
              : true;
          }
        ),
      customFieldTypeId: Yup.number().required(t('settings:customFields.typeRequired')),
      options: Yup.array().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
        customFieldTypeId === CustomFieldType.Dropdown
          ? Yup.array()
              .of(Yup.object().shape({ name: Yup.string().required() }))
              .test(
                'check-unique-option-name',
                t('settings:customFields.validation.dontUseDuplicateOptions'),
                array => array!.length === new Set(array!.map((a: any) => a.name)).size
              )
              .min(1, t('settings:customFields.validation.atLeastOneOptionIsRequired'))
          : schema
      ),
      headers: Yup.array().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
        customFieldTypeId === CustomFieldType.Table
          ? Yup.array()
              .of(Yup.object().shape({ name: Yup.string().required() }))
              .test(
                'check-unique-header-name',
                t('settings:customFields.validation.dontUseDuplicateOptions'),
                array => array!.length === new Set(array!.map((a: any) => a.name)).size
              )
              .min(1, t('settings:customFields.validation.atLeastOneOptionIsRequired'))
          : schema
      ),
      tableDelimiter: Yup.string().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
        customFieldTypeId === CustomFieldType.Table ? getTableDelimiterValidation(t) : schema
      ),
      tableRowDelimiter: Yup.string().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
        customFieldTypeId === CustomFieldType.Table ? getTableRowDelimiterValidation(t) : schema
      ),
      productRelationDelimiter: Yup.string().when(
        ['customFieldTypeId'],
        ([customFieldTypeId], schema) =>
          customFieldTypeId === CustomFieldType.Product_Relation
            ? getProductRelationDelimiterValidation(t)
            : schema.nullable()
      ),
      productRelationBrandCodeDelimiter: Yup.string().when(
        ['customFieldTypeId'],
        ([customFieldTypeId], schema) =>
          customFieldTypeId === CustomFieldType.Product_Relation
            ? getProductRelationBrandCodeDelimiterValidation(t)
            : schema.nullable()
      ),
    });
  };

  const getDefaultValues = (): FormData => ({
    name: customField ? customField.name : '',
    description: customField?.description || '',
    customFieldTypeId: customField ? customField.custom_field_type_id : CustomFieldType.Input, // Default to text/input type
    options:
      customField && customField.options
        ? customField.options.sort((a, b) => a.record_number - b.record_number)
        : [],
    headers:
      customField && customField.headers
        ? customField.headers.sort((a, b) => a.record_number - b.record_number)
        : [],
    tableDelimiter: customField?.table_delimiter || '|',
    tableRowDelimiter: customField?.table_row_delimiter || '~n~',
    productRelationDelimiter: customField?.product_relation_delimiter || ',',
    productRelationBrandCodeDelimiter: customField?.product_relation_brand_code_delimiter || null,
    publishChanges: customField ? !customField.ignore_on_sync : true,
    scriptId: customField && customField.script_id ? customField.script_id : null,
    groupId: customField ? customField.group_id : null,
    enhancedValidationIds:
      customField && customField.enhanced_validation_ids
        ? customField.enhanced_validation_ids.split(',').map(id => Number(id.trim()))
        : [],
  });

  const handleSubmit = async (values: FormData) => {
    const enhancedValidationIdsString =
      values.enhancedValidationIds && values.enhancedValidationIds.length > 0
        ? values.enhancedValidationIds.join(',')
        : null;

    return dispatch(
      updateCustomField({
        customFieldId: customField ? customField.id : null,
        name: values.name,
        description: values.description || null,
        customFieldTypeId: values.customFieldTypeId,
        recordNumber: customField ? customField.record_number : customFieldsLength + 1,
        ignoreOnSync: values.publishChanges ? 0 : 1,
        groupId: (() => {
          if (values.groupId === 'other') {
            return null;
          }
          if (typeof values.groupId === 'string') {
            return Number(values.groupId);
          }
          return values.groupId;
        })(),
        enhancedValidationIds: enhancedValidationIdsString,
        ...(values.customFieldTypeId === CustomFieldType.Dropdown && { options: values.options }),
        ...(values.customFieldTypeId === CustomFieldType.Table && {
          headers: values.headers,
          tableDelimiter: values.tableDelimiter,
          tableRowDelimiter: values.tableRowDelimiter,
        }),
        ...(values.customFieldTypeId === CustomFieldType.Product_Relation && {
          productRelationDelimiter: values.productRelationDelimiter,
          productRelationBrandCodeDelimiter: values.productRelationBrandCodeDelimiter,
        }),
        ...(values.customFieldTypeId === CustomFieldType.Button &&
          values.scriptId && { scriptId: values.scriptId }),
      })
    )
      .then(result => {
        message.success(
          customField
            ? t('settings:customFields.customFieldUpdated')
            : t('settings:customFields.customFieldCreated')
        );
        onFieldSaved();
        closeDrawer();
        return result;
      })
      .catch(error => {
        const errorMessage =
          error.response?.data?.message || t('settings:customFields.failedToSave');
        message.error(errorMessage);
        throw error;
      });
  };

  const handleDelete = async () => {
    if (customField && onDelete) {
      try {
        await onDelete(customField.id);
        closeDrawer();
      } catch (error) {
        // Error handling is done in the action
      }
    }
  };

  return (
    <DrawerHookForm
      key={customField?.id || 'new-custom-field'}
      title={
        customField
          ? t('settings:customFields.editCustomField')
          : t('settings:customFields.addCustomField')
      }
      visible={visible}
      onClose={closeDrawer}
      width={450}
      validationSchema={getValidationSchema()}
      defaultValues={getDefaultValues()}
      onSubmit={handleSubmit}
      handleSaveButtonEnabled={(formState: any) => canEditCustomFields && formState.isDirty}
      onDelete={customField && onDelete ? handleDelete : undefined}
      deleteButtonEnabled={!!customField && !!onDelete && canEditCustomFields}
      isLoading={isDeleting}
      destroyOnClose
    >
      {({ watch, setValue, control }: any) => {
        const values = watch() as FormData;

        const handleAddOption = () => {
          const maxRecordNumber =
            values.options.length > 0
              ? Math.max(...values.options.map(opt => opt.record_number))
              : 0;
          setValue(
            'options',
            [...values.options, { name: '', record_number: maxRecordNumber + 1 }],
            { shouldDirty: true }
          );
        };

        const handleUpdateOptions = (options: CustomFieldOption[]) => {
          setValue('options', options, { shouldDirty: true });
        };

        return (
          <Form layout="vertical">
            <FormInput name="name" label={t('common:name')} required useHookForm />

            <Controller
              name="description"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label={t('common:description')}
                  validateStatus={error ? 'error' : ''}
                  help={error?.message || undefined}
                >
                  <Input.TextArea {...field} rows={3} placeholder={t('common:description')} />
                </Form.Item>
              )}
            />

            <FormSelect
              name="customFieldTypeId"
              label={t('common:type')}
              values={customFieldTypes}
              required
              useHookForm
            />

            {values.customFieldTypeId === CustomFieldType.Dropdown && (
              <div className="flex-col">
                <Button type="primary" ghost onClick={handleAddOption}>
                  {t('settings:customFields.addNewOption')}
                </Button>
                {values.options && values.options.length > 0 && (
                  <SettingsCustomFieldOptions
                    options={values.options}
                    updateOptions={handleUpdateOptions}
                  />
                )}
              </div>
            )}

            {values.customFieldTypeId === CustomFieldType.Table && (
              <div className="flex-col">
                <Button
                  type="primary"
                  ghost
                  onClick={() => {
                    const maxRecordNumber =
                      values.headers.length > 0
                        ? Math.max(...values.headers.map(header => header.record_number))
                        : 0;
                    setValue(
                      'headers',
                      [...values.headers, { name: '', record_number: maxRecordNumber + 1 }],
                      { shouldDirty: true }
                    );
                  }}
                >
                  {t('settings:customFields.addColumnHeader')}
                </Button>
                {values.headers && values.headers.length > 0 && (
                  <SettingsCustomFieldOptions
                    options={values.headers}
                    updateOptions={(headers: CustomFieldOption[]) => {
                      setValue('headers', headers, { shouldDirty: true });
                    }}
                  />
                )}
                <FormInput
                  name="tableDelimiter"
                  label={t('settings:customFields.tableDelimiter')}
                  required
                  useHookForm
                  className="mt-5"
                />
                <FormInput
                  name="tableRowDelimiter"
                  label={t('settings:customFields.tableRowDelimiter')}
                  required
                  useHookForm
                />
              </div>
            )}

            {values.customFieldTypeId === CustomFieldType.Product_Relation && (
              <>
                <FormInput
                  name="productRelationDelimiter"
                  label={t('settings:customFields.productRelationDelimiter')}
                  required
                  useHookForm
                  className="mt-5"
                />
                <FormInput
                  name="productRelationBrandCodeDelimiter"
                  label={
                    <>
                      {t('settings:customFields.productRelationBrandCodeDelimiter')}
                      {!customField && (
                        <AntTooltip
                          title={t(
                            'settings:customFields.productRelationBrandCodeDelimiterTooltip'
                          )}
                        >
                          <InfoCircleOutlined className="ml-1 icon__blue" />
                        </AntTooltip>
                      )}
                    </>
                  }
                  disabled={!!customField}
                  useHookForm
                />
              </>
            )}

            {values.customFieldTypeId === CustomFieldType.Button && (
              <FormSelect
                name="scriptId"
                label={t('settings:customFields.script')}
                values={scripts}
                loading={fetchingScripts}
                required
                useHookForm
              />
            )}

            <FormSelect
              name="groupId"
              label={t('common:group')}
              values={groupOptions}
              allowClear
              useHookForm
            />

            <FormSelect
              name="enhancedValidationIds"
              label={t('settings:customFields.enhancedValidations')}
              values={enhancedValidations}
              mode="multiple"
              placeholder={t('settings:customFields.selectEnhancedValidations')}
              useHookForm
            />

            <Typography.Title level={5} className="mt-5">
              {t('settings:customFields.customfieldSettings')}
            </Typography.Title>
            <div className="flex">
              <FormSwitch
                name="publishChanges"
                checkedChildren={t('common:yes')}
                unCheckedChildren={t('common:no')}
                useHookForm
              />
              <span className="h-8 ml-3 leading-8">
                {t('settings:customFields.updateTimeStamp')}
              </span>
            </div>
          </Form>
        );
      }}
    </DrawerHookForm>
  );
};

export default ParentCustomFieldEditDrawer;
